import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, CheckCircle, FileText } from 'lucide-react';
import { StudentLayout } from '../../components/layout/StudentLayout';
import { authApi, studentExamApi } from '../../api/examApi';
import { useAuth } from '../../context/AuthContext';
import {
  useExamAccess,
  ExamAccessBanner,
  ExamAccessBlockedButton,
} from '../../components/student/ExamAccessGate';
import { toast } from 'sonner';
import { PaperTestBadge, PaperTestAction } from '../../components/student/PaperTestStatus';

// The exam row's meta line has to wrap on narrow viewports, so the bullet
// separators ride along as an ::after on the item they follow rather than
// living in their own spans - a standalone separator span would orphan itself
// at the start of a wrapped line.
const META_SEPARATOR =
  "after:ml-4 after:text-slate-300 after:content-['•'] dark:after:text-slate-600";

export default function StudentDashboard() {
  const [exams, setExams] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const access = useExamAccess();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [examsRes, attemptsRes] = await Promise.all([
        studentExamApi.getAvailableExams(),
        studentExamApi.getMyAttempts(),
      ]);
      setExams(examsRes.data);
      setAttempts(attemptsRes.data);
      const profileRes = await authApi.getStudentProfile();
      setProfile(profileRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getAttemptForExam = (examId) => {
    return attempts.find(a => a.exam_id === examId);
  };

  const stats = {
    // Paper tests are not startable online, so they are not "available".
    available: exams.filter(e => e.conduct_mode !== 'offline' && !getAttemptForExam(e.id)).length,
    inProgress: attempts.filter(a => a.status === 'in_progress').length,
    completed: attempts.filter(a => ['submitted', 'evaluated'].includes(a.status)).length,
    evaluated: attempts.filter(a => a.status === 'evaluated').length,
  };

  const profileComplete = Boolean(
    profile?.phone_number &&
    profile?.class_level &&
    profile?.subjects?.length > 0
  );

  return (
    <StudentLayout title="Student Dashboard">
      <div className="animate-fade-in space-y-6">
        <div className="mb-6">
          <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
            Welcome, {user?.name}!
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Your exam portal dashboard
          </p>
        </div>

        {!profileComplete && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 dark:border-amber-900/40 dark:bg-amber-900/10">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="font-semibold text-amber-900 dark:text-amber-200">Complete your student profile</h3>
                <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                  Add your class, phone number, and subjects so the institute has a proper student record for you.
                </p>
              </div>
              <Link
                to="/student/profile"
                className="inline-flex items-center justify-center rounded-md bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600"
              >
                Update Profile
              </Link>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Available Exams</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stats.available}</p>
              </div>
              <div className="p-3 bg-primary/10 dark:bg-accent-cool/10 rounded-lg">
                <BookOpen className="text-primary dark:text-accent-cool" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">In Progress</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{stats.inProgress}</p>
              </div>
              <div className="p-3 bg-amber-100 dark:bg-amber-900/20 rounded-lg">
                <Clock className="text-amber-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Submitted</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{stats.completed}</p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <FileText className="text-blue-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Evaluated</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">{stats.evaluated}</p>
              </div>
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg">
                <CheckCircle className="text-emerald-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Available Exams */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <BookOpen size={20} className="text-primary dark:text-accent-cool" />
            Available Exams
          </h3>

          {!access.loading && !access.allowed && (
            <div className="mb-4">
              <ExamAccessBanner status={access.status} />
            </div>
          )}

          {loading ? (
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 bg-slate-100 dark:bg-slate-700 rounded-lg" />
              ))}
            </div>
          ) : exams.length === 0 ? (
            <p className="text-slate-500 dark:text-slate-400 text-center py-8">
              No exams available at the moment
            </p>
          ) : (
            <div className="space-y-3">
              {exams.map(exam => {
                const attempt = getAttemptForExam(exam.id);
                const isPaper = exam.conduct_mode === 'offline';
                const canStart = !isPaper && (!attempt || attempt.status === 'in_progress');
                
                return (
                  <div
                    key={exam.id}
                    className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-primary/50 dark:hover:border-accent-cool/50 transition-colors"
                    data-testid={`exam-card-${exam.id}`}
                  >
                    <div className="min-w-0 flex-1 basis-56">
                      <h4 className="font-medium text-slate-900 dark:text-white break-words">{exam.title}</h4>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-slate-500 dark:text-slate-400">
                        <span className={META_SEPARATOR}>{exam.subject}</span>
                        <span className={META_SEPARATOR}>{isPaper ? `Paper test${exam.exam_date ? ` · ${exam.exam_date}` : ''}` : `${exam.duration_minutes} min`}</span>
                        <span className={META_SEPARATOR}>{exam.total_marks} marks</span>
                        <span className="capitalize">{exam.exam_type.replace('_', ' ')}</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 max-w-full flex-wrap items-center gap-3">
                      {isPaper && <PaperTestBadge exam={exam} />}
                      {!isPaper && attempt && attempt.status !== 'in_progress' && (
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          attempt.status === 'evaluated' 
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        }`}>
                          {attempt.status === 'evaluated' ? `Score: ${attempt.total_score}/${exam.total_marks}` : 'Submitted'}
                        </span>
                      )}
                      {isPaper ? (
                        <PaperTestAction exam={exam} />
                      ) : canStart && !access.allowed ? (
                        <ExamAccessBlockedButton />
                      ) : canStart ? (
                        <Link
                          to={attempt ? `/student/exam/${exam.id}/attempt/${attempt.id}` : `/student/exam/${exam.id}`}
                          className="px-4 py-2 bg-primary hover:bg-primary-light text-white rounded-lg text-sm font-medium transition-colors"
                          data-testid={`start-exam-${exam.id}`}
                        >
                          {attempt ? 'Continue' : 'Start Exam'}
                        </Link>
                      ) : (
                        <Link
                          to={`/student/results/${attempt.id}`}
                          className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-lg text-sm font-medium transition-colors"
                        >
                          View Result
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Attempts */}
        {attempts.length > 0 && (
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <FileText size={20} className="text-secondary" />
              Recent Attempts
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                    <th className="pb-3 font-medium">Exam</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">MCQ Score</th>
                    <th className="pb-3 font-medium">Subjective</th>
                    <th className="pb-3 font-medium">Total</th>
                    <th className="pb-3 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {attempts.slice(0, 5).map(attempt => {
                    const exam = exams.find(e => e.id === attempt.exam_id);
                    return (
                      <tr key={attempt.id} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-3 text-slate-900 dark:text-white">{exam?.title || 'Unknown'}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                            attempt.status === 'evaluated' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                            attempt.status === 'submitted' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                            attempt.status === 'in_progress' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                            'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                          }`}>
                            {attempt.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-3 text-slate-700 dark:text-slate-300">{attempt.mcq_score ?? '-'}</td>
                        <td className="py-3 text-slate-700 dark:text-slate-300">{attempt.subjective_score ?? '-'}</td>
                        <td className="py-3 font-medium text-slate-900 dark:text-white">{attempt.total_score ?? '-'}</td>
                        <td className="py-3">
                          <Link
                            to={`/student/results/${attempt.id}`}
                            className="text-primary dark:text-accent-cool hover:underline text-sm"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
}
